// spec 260509-system-monitor patch B-7：supervised 服务卡共享渲染。
//
// 这个组件给 **Dashboard ServicesSection** 与 **桌面 SystemHealthWidget 第 ④
// 段（Stage C）** 共用，避免两处分叉同款 supervised 卡的样式与异常判定。
//
// **不**复用首页 ``ServiceStatusCards`` —— 那里走 button 形态 + 5 张固定卡 +
// 网络卡可点击，语义和 supervised 卡有本质差异（首页是"几大基础服务一眼健康
// 度"，dashboard / widget 关心的是 supervised 容器卡），强行合并会让 props
// 表面积爆炸。两类卡走两条独立路径。

import type { ReactElement } from 'react';

import type { ServiceCard } from './state/system-types';

export function isServiceUnhealthy(s: ServiceCard): boolean {
  const dur = s.estimated_unhealthy_for_seconds;
  if (typeof dur === 'number' && dur >= 10) return true;
  if (s.lifecycle_state === 'down' || s.lifecycle_state === 'restarting') return true;
  if (s.status === 'failed' || s.status === 'unavailable') return true;
  return false;
}

function ModelMetricsCompactRow({ s }: { s: ServiceCard }): ReactElement | null {
  const m = s.metrics;
  if (!m) return null;
  return (
    <div className="dash-svc-card-metrics">
      {m.running !== null && <span>▶ {m.running}</span>}
      {m.waiting !== null && <span>⏸ {m.waiting}</span>}
      {m.kv_cache_perc !== null && (
        <span>{(m.kv_cache_perc * 100).toFixed(0)}% KV</span>
      )}
      {m.tokens_per_sec !== null && (
        <span>{m.tokens_per_sec.toFixed(1)} tok/s</span>
      )}
      {m.ttft_p95_ms !== null && <span>{m.ttft_p95_ms.toFixed(0)} ms</span>}
    </div>
  );
}

interface Props {
  s: ServiceCard;
  /** 用作 anchor id 前缀；dashboard 用 "services-"，widget 用 "widget-svc-"。 */
  anchorPrefix?: string;
}

export function ServiceCardItem({ s, anchorPrefix = 'services-' }: Props): ReactElement {
  const isModel = s.kind === 'llm' || s.kind === 'asr';
  const unhealthy = isServiceUnhealthy(s);
  const anchorId = `${anchorPrefix}${s.name}`;
  return (
    <article
      id={anchorId}
      data-anchor={anchorId}
      className={[
        'dash-svc-card',
        s.tier === 'critical' ? 'is-critical' : null,
        unhealthy ? 'is-unhealthy' : null,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="dash-svc-card-header">
        <h3>{s.display_name ?? s.name}</h3>
        <span className="dash-svc-card-status">{s.lifecycle_state ?? s.status}</span>
      </header>
      {s.last_error && <div className="dash-svc-card-error">{s.last_error}</div>}
      {isModel && <ModelMetricsCompactRow s={s} />}
      {isModel && s.metrics?.deeplink && (
        <a
          className="dash-svc-card-grafana"
          href={s.metrics.deeplink}
          target="_blank"
          rel="noopener noreferrer"
        >
          📊 在 Grafana 查看
        </a>
      )}
    </article>
  );
}

export default ServiceCardItem;
